import { EAddressType, type EStoreIds } from "../../../constants";
import type {
  IUseAddressFormSetters,
  IUseAddressFormStates,
} from "../../../hooks";
import type { IAddress, TranslationFn } from "../../../types";
import { getFormFieldRequirements, isPhoneNumberValid } from "../../../utils";

export const setError = (
  addressFormSetters: IUseAddressFormSetters,
  field: keyof IUseAddressFormStates["errorStates"],
  value: string | boolean,
): void => {
  addressFormSetters.setErrorStates((prevStates) => ({
    ...prevStates,
    [field]: value,
  }));
};

export const setQatarFieldsError = (
  addressFormSetters: IUseAddressFormSetters,
  field: keyof IUseAddressFormStates["errorStates"],
  value: boolean,
): void => {
  addressFormSetters.setErrorStates((prevStates) => ({
    ...prevStates,
    [field]: value,
  }));
};

export const validateAddressType = (
  addressFormSetters: IUseAddressFormSetters,
  addressFormStates: IUseAddressFormStates,
  t: TranslationFn,
): boolean => {
  const { addressType } = addressFormStates.otherStates;
  const isAddressTypeValid = addressType[0] !== "";

  addressFormSetters.setErrorStates((prevStates) => ({
    ...prevStates,
    addressTypeError: isAddressTypeValid
      ? ""
      : t("addressJourney.error.addressType"),
  }));

  return isAddressTypeValid;
};

export const validateFloor = (
  addressFormSetters: IUseAddressFormSetters,
  floor: string,
  t: TranslationFn,
): boolean => {
  if (floor === "") {
    setError(
      addressFormSetters,
      "floorError",
      t("addressJourney.error.floor.required"),
    );
    return false;
  }
  const isFloorNumber = /^\d/.test(floor || "");
  if (!isFloorNumber) {
    setError(
      addressFormSetters,
      "floorError",
      t("addressJourney.error.floor.invalid"),
    );
    return false;
  }
  setError(addressFormSetters, "floorError", "");
  return true;
};

export const validateNumberField = (
  addressFormSetters: IUseAddressFormSetters,
  number: string,
  t: TranslationFn,
): boolean => {
  if (number === "") {
    setError(
      addressFormSetters,
      "numberError",
      t("addressJourney.error.number.required"),
    );
    return false;
  }
  const isNumber = /^\d+$/.test(number);
  if (!isNumber) {
    setError(
      addressFormSetters,
      "numberError",
      t("addressJourney.error.number.invalid"),
    );
    return false;
  }
  setError(addressFormSetters, "numberError", "");
  return true;
};

export const validateFormFields = (
  formData: IAddress,
  addressFormSetters: IUseAddressFormSetters,
  addressFormStates: IUseAddressFormStates,
  storeId: EStoreIds,
  t: TranslationFn,
): boolean => {
  const { emirate, town, appartment, building, streetName } = formData;

  if (!building || !streetName || !(emirate || town)) return false;

  const { isKuwaitFormFieldRequired } = getFormFieldRequirements(storeId);
  if (isKuwaitFormFieldRequired) {
    const { number, floor, block } = formData;
    const isNumberValid = validateNumberField(
      addressFormSetters,
      number || "",
      t,
    );
    const isFloorValid = validateFloor(addressFormSetters, floor || "", t);
    return Boolean(isNumberValid && isFloorValid && block);
  }

  const { addressType } = addressFormStates.otherStates;
  const isAppartmentRequired =
    addressType[0] !== (EAddressType.OTHER as unknown as string);
  if (isAppartmentRequired && !appartment) return false;
  return true;
};

export const validateReceiverDetails = (
  formData: IAddress,
  addressFormSetters: IUseAddressFormSetters,
): boolean => {
  const { firstName, countryCode, phone } = formData;
  const isPhoneValid =
    countryCode && phone
      ? isPhoneNumberValid(`${countryCode}${phone}`, countryCode)
      : false;

  addressFormSetters.setErrorStates((prevStates) => ({
    ...prevStates,
    invalidPhoneNumber: !isPhoneValid,
  }));

  if (!firstName || !isPhoneValid) {
    addressFormSetters.setReceiverDetails((prevStates) => ({
      ...prevStates,
      changeReceiverDetails: true,
    }));
    return false;
  }
  return true;
};

export const validateQatarFields = (
  addressFormSetters: IUseAddressFormSetters,
  addressFormStates: IUseAddressFormStates,
): boolean => {
  const { selectedArea, selectedCity, selectedZone } =
    addressFormStates.qatarStates;

  const errors = {
    cityError: selectedCity === "",
    zoneError: selectedZone === "",
    areaError: selectedArea === "",
  };

  Object.entries(errors).forEach(([field, hasError]) => {
    setQatarFieldsError(
      addressFormSetters,
      field as keyof IUseAddressFormStates["errorStates"],
      hasError,
    );
  });

  return !Object.values(errors).some(Boolean);
};
